// Rotas de SMS Premium/Marketing (Comtele) — aba "Pacotes SMS" de
// Configurações (super admin). Cada rota tem o id da rota na Comtele e o peso
// (créditos que cada SMS consome). Marketing vazio = rota padrão do servidor;
// Premium vazio = opção escondida nas campanhas.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS } from "../../styles/aerosTokens";

const ROTAS = ["marketing", "premium"];
const t = (k, o) => i18n.t(`aeros.settings.smsRotas.${k}`, o);

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: "16px 18px",
      minWidth: 0,
      marginTop: 16,
    },
    title: { fontSize: 13, fontWeight: 700, color: theme.palette.text.primary, marginBottom: 6 },
    help: { fontSize: 11.5, color: theme.palette.aeros.textTertiary, marginBottom: 12 },
    row: {
      display: "grid",
      gridTemplateColumns: "100px minmax(0,1fr) minmax(0,1fr)",
      gap: 10,
      alignItems: "end",
      marginBottom: 10,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    rotaNome: { fontSize: 13, fontWeight: 700, color: theme.palette.text.primary, paddingBottom: 9 },
    actions: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6 },
    botao: {
      borderRadius: 9,
      fontWeight: 700,
      fontSize: 12.5,
      textTransform: "none",
      minHeight: 44,
      padding: "9px 14px",
      background: `linear-gradient(135deg, ${AEROS.defaultPrimary}, ${AEROS.cyan})`,
      color: "#07080D",
      "&.Mui-disabled": { opacity: 0.5, color: "#07080D" },
    },
    botaoSec: {
      borderRadius: 9,
      fontWeight: 600,
      fontSize: 12.5,
      textTransform: "none",
      minHeight: 44,
      padding: "9px 14px",
      border: `1px solid ${border}`,
      color: theme.palette.text.primary,
    },
    lista: { marginTop: 12, display: "flex", flexDirection: "column", gap: 6 },
    item: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "8px 12px",
      border: `1px solid ${border}`,
      borderRadius: 10,
      fontSize: 12.5,
      color: theme.palette.text.primary,
      minWidth: 0,
    },
    itemTexto: { minWidth: 0, overflowWrap: "anywhere" },
    itemBotoes: { display: "flex", gap: 6, flexWrap: "wrap" },
    usar: {
      minHeight: 44,
      minWidth: 44,
      textTransform: "none",
      fontSize: 12,
      fontWeight: 600,
      borderRadius: 8,
      border: `1px solid ${border}`,
    },
    vazio: { marginTop: 12, fontSize: 12.5, color: theme.palette.aeros.textTertiary },
  };
});

const VAZIO = { marketing: { routeId: "", peso: "1" }, premium: { routeId: "", peso: "2" } };

const paraForm = (data) =>
  ROTAS.reduce((acc, r) => {
    const v = data?.[r] || {};
    acc[r] = {
      routeId: v.routeId === null || v.routeId === undefined ? "" : String(v.routeId),
      peso: v.peso === null || v.peso === undefined ? "1" : String(v.peso),
    };
    return acc;
  }, {});

const SmsRotasManager = () => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [form, setForm] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [rotasComtele, setRotasComtele] = useState(null);

  const carrega = useCallback(async () => {
    try {
      const { data } = await api.get("/sms/admin/rotas");
      setForm(paraForm(data));
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    carrega();
  }, [carrega]);

  const altera = (rota, campo, valor) =>
    setForm((f) => ({ ...f, [rota]: { ...f[rota], [campo]: valor } }));

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      const body = {};
      ROTAS.forEach((r) => {
        const id = String(form[r].routeId).trim();
        body[r] = { routeId: id === "" ? null : Number(id), peso: Number(form[r].peso) };
      });
      const { data } = await api.put("/sms/admin/rotas", body);
      if (data) setForm(paraForm(data));
      toast.success(t("saved"));
    } catch (err) {
      toastError(err);
    }
    setSalvando(false);
  };

  const handleConsultar = async () => {
    setConsultando(true);
    try {
      const { data } = await api.get("/sms/admin/rotas/comtele");
      setRotasComtele(Array.isArray(data) ? data : []);
    } catch (err) {
      setRotasComtele(null);
      toastError(err);
    }
    setConsultando(false);
  };

  return (
    <div className={classes.card}>
      <div className={classes.title}>{t("title")}</div>
      <div className={classes.help}>{t("help")}</div>

      {ROTAS.map((r) => (
        <div className={classes.row} key={r}>
          <div className={classes.rotaNome}>{t(r)}</div>
          <div>
            <label className={m.fieldLabel} htmlFor={`sms-rota-${r}-id`}>{t("routeId")}</label>
            <TextField
              id={`sms-rota-${r}-id`}
              fullWidth
              type="number"
              variant="outlined"
              className={`${m.input} ${m.inputSmall}`}
              value={form[r].routeId}
              onChange={(e) => altera(r, "routeId", e.target.value)}
              inputProps={{ min: 1, step: 1, inputMode: "numeric" }}
            />
          </div>
          <div>
            <label className={m.fieldLabel} htmlFor={`sms-rota-${r}-peso`}>{t("weight")}</label>
            <TextField
              id={`sms-rota-${r}-peso`}
              fullWidth
              type="number"
              variant="outlined"
              className={`${m.input} ${m.inputSmall}`}
              value={form[r].peso}
              onChange={(e) => altera(r, "peso", e.target.value)}
              inputProps={{ min: 1, step: 1, inputMode: "numeric" }}
            />
          </div>
        </div>
      ))}

      <div className={classes.actions}>
        <Button className={classes.botao} onClick={handleSalvar} disabled={salvando}>
          {t("save")}
        </Button>
        <Button className={classes.botaoSec} onClick={handleConsultar} disabled={consultando}>
          {t("lookup")}
        </Button>
      </div>

      {rotasComtele && rotasComtele.length === 0 && (
        <div className={classes.vazio}>{t("lookupEmpty")}</div>
      )}
      {rotasComtele && rotasComtele.length > 0 && (
        <div className={classes.lista}>
          {rotasComtele.map((rt) => (
            <div className={classes.item} key={rt.id}>
              <span className={classes.itemTexto}>
                {`${rt.id} · ${rt.displayName} · ${rt.farePrice}`}
              </span>
              <span className={classes.itemBotoes}>
                {ROTAS.map((r) => (
                  <Button
                    key={r}
                    className={classes.usar}
                    onClick={() => altera(r, "routeId", String(rt.id))}
                    aria-label={`${t("useFor")} ${t(r)}: ${rt.id}`}
                  >
                    {`${t("useFor")} ${t(r)}`}
                  </Button>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SmsRotasManager;
